import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  useTheme,
  Tooltip,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useBrand } from '../../context/BrandContext';
import { useInventoryBrand } from '../../hooks/useInventoryBrand';
import { excludeCompanyLevel } from '../../utils/brandFilters';
import api from '../../config/api';

/**
 * ASIN Mapping Component for Inventory Section
 * Manages ASIN to Brand mappings for Amazon product identification
 * Used for correct inventory deductions based on ASIN matching priority
 *
 * @param {boolean} embedded - If true, hides header/brand selector (for embedding in ProductCOGS)
 */
const AsinMapping = ({ embedded = false }) => {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const theme = useTheme();
  const { selectedBrand, brands, selectBrand } = useInventoryBrand();
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Mapped ASINs state
  const [mappedAsins, setMappedAsins] = useState([]);
  const [mappedPage, setMappedPage] = useState(0);
  const [mappedRowsPerPage, setMappedRowsPerPage] = useState(25);
  const [mappedTotalCount, setMappedTotalCount] = useState(0);
  const [searchAsin, setSearchAsin] = useState('');
  // Initialised from the page's selected brand so the very first fetch is
  // already scoped (a late unfiltered response must never win the race).
  const [filterBrand, setFilterBrand] = useState(selectedBrand?.name || '');

  // Unmapped ASINs state
  const [unmappedAsins, setUnmappedAsins] = useState([]);

  // Available brands dropdown
  const [availableBrands, setAvailableBrands] = useState([]);

  // Products for brand (for cascading dropdown)
  const [productsForBrand, setProductsForBrand] = useState({});  // { brandId: [products] }

  // Editing state - now tracks { asin: { brandId, brandName, productMasterId, sku } }
  const [editingRows, setEditingRows] = useState({});

  // Fetch brands + unmapped count on initial load. The mapped list is fetched
  // by the effect below (which already fires on mount) — a second unfiltered
  // fetch here raced the brand-scoped one and could overwrite it.
  useEffect(() => {
    fetchBrands();
    fetchUnmappedAsins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when tab changes or pagination changes
  useEffect(() => {
    if (activeSubTab === 0) {
      fetchMappedAsins();
    } else {
      fetchUnmappedAsins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, mappedPage, mappedRowsPerPage, filterBrand]);

  // The mapped list follows the PAGE's selected brand (a SandandSky page must
  // not show NaturesBaby mappings). Embedded (product page) hides the internal
  // dropdown entirely — the top Brand filter is the single source of truth,
  // same as the Bundles/Parts/Products tabs (Rishabh 2026-07-03). The
  // standalone page keeps the dropdown incl. "All Brands".
  useEffect(() => {
    setFilterBrand(selectedBrand?.name || '');
    setMappedPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      selectBrand(brand);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/api/asin-mappings/brands');
      setAvailableBrands(response.data.data.brands || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
      setError('Failed to load brands');
    }
  };

  const fetchMappedAsins = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: mappedPage + 1,
        limit: mappedRowsPerPage
      };

      if (searchAsin) params.search = searchAsin;
      if (filterBrand) params.brand = filterBrand;

      const response = await api.get('/api/asin-mappings', { params });

      setMappedAsins(response.data.data.mappings || []);
      setMappedTotalCount(response.data.data.pagination.total || 0);

    } catch (err) {
      console.error('Error fetching mapped ASINs:', err);
      setError(err.response?.data?.message || 'Failed to load mapped ASINs');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnmappedAsins = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/api/asin-mappings/unmapped', {
        params: { limit: 1000 }
      });

      const asins = response.data.data.unmappedAsins || [];
      setUnmappedAsins(asins);

      // Pre-fetch products for any brands that have saved/suggested values
      const brandIdsToFetch = new Set();
      asins.forEach(item => {
        if (item.saved_brand_id && item.saved_brand_id > 0) {
          brandIdsToFetch.add(item.saved_brand_id);
        } else if (item.suggested_brand_id && item.suggested_brand_id > 0) {
          brandIdsToFetch.add(item.suggested_brand_id);
        }
      });

      // Fetch products for each brand (with includeAll=true for unmapped tab)
      brandIdsToFetch.forEach(brandId => {
        const cacheKey = `${brandId}_all`;
        if (!productsForBrand[cacheKey]) {
          api.get(`/api/asin-mappings/products/${brandId}`, { params: { includeAll: 'true' } })
            .then(res => {
              setProductsForBrand(prev => ({
                ...prev,
                [cacheKey]: res.data.data.products || []
              }));
            })
            .catch(err => console.error('Error prefetching products for brand:', brandId, err));
        }
      });

    } catch (err) {
      console.error('Error fetching unmapped ASINs:', err);
      setError(err.response?.data?.message || 'Failed to load unmapped ASINs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubTabChange = (event, newValue) => {
    setActiveSubTab(newValue);
    setError('');
    setSuccess('');
  };

  // Fetch products for a specific brand (for cascading dropdown)
  // includeAll=true returns ALL products, false returns only products without ASINs
  const fetchProductsForBrand = async (brandId, includeAll = false) => {
    const cacheKey = includeAll ? `${brandId}_all` : `${brandId}`;
    if (productsForBrand[cacheKey]) {
      return; // Already cached
    }
    try {
      const params = includeAll ? { includeAll: 'true' } : {};
      const response = await api.get(`/api/asin-mappings/products/${brandId}`, { params });
      setProductsForBrand(prev => ({
        ...prev,
        [cacheKey]: response.data.data.products || []
      }));
    } catch (err) {
      console.error('Error fetching products for brand:', err);
    }
  };

  // Helper to get the correct cache key based on tab
  const getProductsCacheKey = (brandId) => {
    return activeSubTab === 1 ? `${brandId}_all` : `${brandId}`;
  };

  const handleEditBrand = (asin, currentBrandId, currentBrandName) => {
    setEditingRows(prev => ({
      ...prev,
      [asin]: { brandId: currentBrandId, brandName: currentBrandName, productMasterId: null, sku: null }
    }));
    if (currentBrandId) {
      // On Unmapped tab (activeSubTab === 1), fetch ALL products
      fetchProductsForBrand(currentBrandId, activeSubTab === 1);
    }
  };

  const handleBrandChangeForAsin = async (asin, brandId) => {
    const brand = availableBrands.find(b => b.id === brandId);
    setEditingRows(prev => ({
      ...prev,
      [asin]: { brandId, brandName: brand?.name || '', productMasterId: null, sku: null }
    }));
    if (brandId) {
      // On Unmapped tab (activeSubTab === 1), fetch ALL products
      fetchProductsForBrand(brandId, activeSubTab === 1);

      // Auto-save brand mapping immediately (no page reload)
      try {
        await api.post('/api/asin-mappings', { asin, brandId });
        // Update local state to show as "brand saved" without reloading
        setUnmappedAsins(prev => prev.map(item =>
          item.asin === asin
            ? { ...item, saved_brand_id: brandId, saved_brand_name: brand?.name || '' }
            : item
        ));
        setSuccess(`Brand "${brand?.name}" saved for ASIN ${asin}`);
        // Clear success message after 2 seconds
        setTimeout(() => setSuccess(''), 2000);
      } catch (err) {
        console.error('Error auto-saving brand:', err);
        // Don't show error - user can still click Map button to retry
      }
    }
  };

  const handleProductChangeForAsin = (asin, productMasterId) => {
    // Get brandId from editingRows OR from the item's saved/suggested brand
    let brandId = editingRows[asin]?.brandId;
    if (!brandId) {
      const item = unmappedAsins.find(i => i.asin === asin);
      if (item) {
        brandId = item.saved_brand_id || item.suggested_brand_id;
      }
    }

    const cacheKey = getProductsCacheKey(brandId);
    const products = productsForBrand[cacheKey] || [];
    const product = products.find(p => p.id === productMasterId);

    setEditingRows(prev => ({
      ...prev,
      [asin]: {
        ...prev[asin],
        brandId, // Ensure brandId is stored
        productMasterId,
        sku: product?.sku || '',
        productName: product?.product_name || ''
      }
    }));
    // Product mapping requires clicking the Map button (row disappears after full mapping)
  };

  const handleSaveMapping = async (asin) => {
    console.log('handleSaveMapping called for ASIN:', asin);
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Get edit data from editingRows, or fall back to item suggestions
      let editData = editingRows[asin];
      console.log('editingRows[asin]:', editData);

      // If no editingRows entry, get data from the item's suggested/saved values
      if (!editData || (!editData.productMasterId && !editData.brandId)) {
        const item = unmappedAsins.find(i => i.asin === asin);
        if (item) {
          const hasSuggestion = item.suggested_product_id && item.suggested_product_id > 0;
          const hasSavedBrand = item.saved_brand_id && item.saved_brand_id > 0;
          editData = {
            brandId: hasSavedBrand ? item.saved_brand_id : (hasSuggestion ? item.suggested_brand_id : null),
            productMasterId: hasSuggestion ? item.suggested_product_id : null,
            sku: hasSuggestion ? item.suggested_sku : null
          };
        }
      }

      const { productMasterId, brandId } = editData || {};

      // Allow saving with just brand (for ad spend attribution) or with full product
      if (!productMasterId && !brandId) {
        setError('Please select at least a brand to map the ASIN to');
        setLoading(false);
        return;
      }

      // If product selected, send productMasterId; otherwise send brandId for brand-only mapping
      const payload = productMasterId
        ? { asin, productMasterId }
        : { asin, brandId };

      console.log('Sending payload:', payload);
      await api.post('/api/asin-mappings', payload);

      const successMsg = productMasterId
        ? `ASIN ${asin} mapped to product successfully`
        : `ASIN ${asin} mapped to brand successfully (no product linked)`;
      setSuccess(successMsg);

      // Remove from editing state
      const newEditingRows = { ...editingRows };
      delete newEditingRows[asin];
      setEditingRows(newEditingRows);

      // Remove from unmapped list if full product mapping was done
      if (productMasterId) {
        setUnmappedAsins(prev => prev.filter(item => item.asin !== asin));
      } else {
        // Refresh the list for brand-only mapping
        if (activeSubTab === 0) {
          fetchMappedAsins();
        } else {
          fetchUnmappedAsins();
        }
      }

    } catch (err) {
      console.error('Error saving ASIN mapping:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      setError(err.response?.data?.message || err.response?.data?.error || `Failed to save mapping: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMapping = async (asin) => {
    if (!await confirm({ title: 'Delete ASIN Mapping', message: `Are you sure you want to delete the mapping for ASIN ${asin}?`, confirmText: 'Delete', severity: 'danger' })) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await api.delete(`/api/asin-mappings/${asin}`);

      setSuccess(`Mapping for ASIN ${asin} deleted successfully`);
      fetchMappedAsins();

    } catch (err) {
      console.error('Error deleting ASIN mapping:', err);
      setError(err.response?.data?.message || 'Failed to delete mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMappedPage = (event, newPage) => {
    setMappedPage(newPage);
  };

  const handleChangeMappedRowsPerPage = (event) => {
    setMappedRowsPerPage(parseInt(event.target.value, 10));
    setMappedPage(0);
  };

  const handleSearch = () => {
    setMappedPage(0);
    fetchMappedAsins();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  return (
    <Box>
      {/* Header - hidden when embedded */}
      {!embedded && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={3} alignItems="center">
              <LinkIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  ASIN Mapping
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Map Amazon ASINs to brands for accurate inventory matching and deductions
                </Typography>
              </Box>
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel>Select Brand</InputLabel>
                <Select
                  value={selectedBrand?.id || ''}
                  onChange={handleBrandChange}
                  label="Select Brand"
                >
                  {excludeCompanyLevel(brands).map(brand => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: embedded ? 2 : 3 }} icon={<WarningIcon />}>
        <Typography variant="body2">
          <strong>Important:</strong> ASIN mappings determine which brand's inventory is deducted for Amazon orders.
          Unmapped ASINs may cause inventory discrepancies. Ensure all Amazon products have correct mappings.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeSubTab}
            onChange={handleSubTabChange}
            sx={{
              '& .MuiTab-root': {
                minWidth: 120,
                px: 3,
                py: 2
              }
            }}
          >
            <Tab label={`Mapped (${mappedTotalCount})`} icon={<LinkIcon />} iconPosition="start" />
            <Tab label={`Unmapped (${unmappedAsins.length})`} icon={<SearchIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <CardContent>
          {/* Mapped ASINs Tab */}
          {activeSubTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search ASIN..."
                  value={searchAsin}
                  onChange={(e) => setSearchAsin(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{ minWidth: 200 }}
                />
                {!embedded && (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Brand</InputLabel>
                    <Select
                      value={filterBrand}
                      label="Filter by Brand"
                      onChange={(e) => {
                        setFilterBrand(e.target.value);
                        setMappedPage(0);
                      }}
                    >
                      <MenuItem value="">All Brands</MenuItem>
                      {availableBrands.map((brand) => (
                        <MenuItem key={brand.id} value={brand.name}>
                          {brand.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchMappedAsins}
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  Refresh
                </Button>
              </Box>

              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              {!loading && (
                <>
                  <TableContainer component={Paper} elevation={0}>
                    <Table sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>GTIN</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>ASIN</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappedAsins.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Typography color="textSecondary" sx={{ py: 3 }}>
                                No mapped products found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          mappedAsins.map((mapping) => {
                            return (
                              <TableRow
                                key={`${mapping.asin}-${mapping.identifier_id}`}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                              >
                                <TableCell component="th" scope="row">
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                    {mapping.sku || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {mapping.product_name || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    {mapping.gtin || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      {mapping.asin}
                                    </Typography>
                                    <a
                                      href={`https://amazon.com/dp/${mapping.asin}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: 'var(--color-primary)', fontSize: '0.75rem' }}
                                    >
                                      View
                                    </a>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={mapping.brand_name}
                                    size="small"
                                    sx={{
                                      bgcolor: `${theme.palette.primary.main}20`,
                                      color: theme.palette.primary.main,
                                      fontWeight: 'medium'
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {formatDate(mapping.last_updated)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Delete mapping">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteMapping(mapping.asin)}
                                      sx={{ color: theme.palette.error.main }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={mappedTotalCount}
                    rowsPerPage={mappedRowsPerPage}
                    page={mappedPage}
                    onPageChange={handleChangeMappedPage}
                    onRowsPerPageChange={handleChangeMappedRowsPerPage}
                  />
                </>
              )}
            </Box>
          )}

          {/* Unmapped ASINs Tab */}
          {activeSubTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  These ASINs appear in your Amazon Ads data but don't have a brand mapping yet.
                  Map them to ensure correct inventory deductions.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchUnmappedAsins}
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  Refresh
                </Button>
              </Box>

              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              {!loading && unmappedAsins.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <LinkIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    All ASINs are mapped!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Every ASIN in your Amazon products has been assigned to a product.
                  </Typography>
                </Box>
              ) : !loading && (
                <TableContainer component={Paper} elevation={0}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>ASIN</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Amazon SKU</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Select Brand</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Select Product</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmappedAsins.map((item) => {
                        // Check if we have a suggestion from the backend
                        const hasSuggestion = item.suggested_product_id && item.suggested_product_id > 0;
                        // Check if there's a saved brand-only mapping
                        const hasSavedBrand = item.saved_brand_id && item.saved_brand_id > 0;

                        // Priority: editingRows > saved brand > suggestion
                        const editData = editingRows[item.asin] || (hasSavedBrand ? {
                          brandId: item.saved_brand_id,
                          brandName: item.saved_brand_name,
                          productMasterId: hasSuggestion ? item.suggested_product_id : null,
                          sku: hasSuggestion ? item.suggested_sku : null,
                          productName: hasSuggestion ? item.suggested_product_name : null
                        } : hasSuggestion ? {
                          brandId: item.suggested_brand_id,
                          brandName: item.suggested_brand_name,
                          productMasterId: item.suggested_product_id,
                          sku: item.suggested_sku,
                          productName: item.suggested_product_name
                        } : {});

                        const brandId = editData.brandId;
                        const cacheKey = brandId ? `${brandId}_all` : null;
                        const products = cacheKey ? (productsForBrand[cacheKey] || []) : [];

                        return (
                          <TableRow
                            key={item.asin}
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                              bgcolor: hasSavedBrand ? 'warning.50' : (hasSuggestion ? 'success.50' : 'inherit')
                            }}
                          >
                            <TableCell component="th" scope="row">
                              {item.asin ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {item.asin}
                                  </Typography>
                                  <a
                                    href={`https://amazon.com/dp/${item.asin}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'var(--color-primary)', fontSize: '0.75rem' }}
                                  >
                                    View
                                  </a>
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }} color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }} color="text.secondary">
                                {item.amazon_sku || '-'}
                              </Typography>
                              {hasSuggestion && item.match_type && (
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', color: 'success.main' }}>
                                  {item.match_type === 'gtin' && `GTIN match: ${item.suggested_gtin}`}
                                  {item.match_type === 'sku_exact' && `SKU match: ${item.suggested_sku}`}
                                  {item.match_type === 'sku_prefix' && `SKU prefix match: ${item.suggested_sku}`}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" sx={{ minWidth: 180 }}>
                                <Select
                                  value={brandId || ''}
                                  onChange={(e) => handleBrandChangeForAsin(item.asin, e.target.value)}
                                  displayEmpty
                                  sx={hasSuggestion && !editingRows[item.asin] ? { bgcolor: 'success.100' } : {}}
                                  renderValue={(selected) => {
                                    if (!selected) return <em>Select Brand...</em>;
                                    const brand = availableBrands.find(b => b.id === selected);
                                    return brand?.name || editData.brandName || 'Unknown';
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    <em>Select Brand...</em>
                                  </MenuItem>
                                  {availableBrands.map((brand) => (
                                    <MenuItem key={brand.id} value={brand.id}>
                                      {brand.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" sx={{ minWidth: 250 }} disabled={!brandId}>
                                <Select
                                  value={editData.productMasterId || ''}
                                  onChange={(e) => handleProductChangeForAsin(item.asin, e.target.value)}
                                  displayEmpty
                                  sx={hasSuggestion && !editingRows[item.asin] ? { bgcolor: 'success.100' } : {}}
                                  renderValue={(selected) => {
                                    if (!selected) return <em>{brandId ? 'Select Product...' : 'Select brand first'}</em>;
                                    // First try to find in products list
                                    const product = products.find(p => p.id === selected);
                                    if (product) return `${product.sku} - ${product.product_name}`;
                                    // Fall back to editData
                                    if (editData.sku && editData.productName) return `${editData.sku} - ${editData.productName}`;
                                    if (editData.sku) return editData.sku;
                                    // Fall back to suggested values
                                    if (item.suggested_product_id === selected && item.suggested_sku) {
                                      return `${item.suggested_sku} - ${item.suggested_product_name || ''}`;
                                    }
                                    return 'Selected';
                                  }}
                                >
                                  <MenuItem value="" disabled>
                                    <em>{brandId ? 'Select Product...' : 'Select brand first'}</em>
                                  </MenuItem>
                                  {/* Show suggested product first if we have one */}
                                  {hasSuggestion && !products.find(p => p.id === item.suggested_product_id) && (
                                    <MenuItem key={item.suggested_product_id} value={item.suggested_product_id}>
                                      {item.suggested_sku} - {item.suggested_product_name} (Suggested)
                                    </MenuItem>
                                  )}
                                  {products.map((product) => (
                                    <MenuItem key={product.id} value={product.id}>
                                      {product.sku} - {product.product_name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="contained"
                                color={hasSavedBrand ? "warning" : (hasSuggestion ? "success" : "primary")}
                                startIcon={<SaveIcon />}
                                onClick={() => handleSaveMapping(item.asin)}
                                disabled={!editData.productMasterId && !editData.brandId}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: 'none'
                                }}
                              >
                                {hasSavedBrand ? (editData.productMasterId ? 'Link Product' : 'Update') : (hasSuggestion ? 'Confirm' : 'Map')}
                              </Button>
                              {hasSavedBrand && (
                                <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 0.5 }}>
                                  Brand saved
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
};

export default AsinMapping;
